'use client';

import React, { useRef } from 'react';
import Image from 'next/image';
import { motion, useScroll, useTransform } from 'framer-motion';
import AppImage from '@/components/ui/AppImage';
import {
  killGermsIcon,
  natural100Icon,
  noHarshChemicalsIcon,
  skinFriendlyIcon,
  labTestedIcon
} from '@/assets/icons';
import { useTranslation } from 'react-i18next';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }
  })
};

const floatingBadge = {
  hidden: { opacity: 0, scale: 0.8, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.8 + i * 0.15, ease: [0.16, 1, 0.3, 1] }
  })
};

export default function HeroSection() {
  const { t } = useTranslation('home');
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });

  // FIX 1: Safely disable heavy transform effects on touch devices to improve scrolling performance
  const bottleY = useTransform(scrollYProgress, [0, 1], [0, typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches ? 0 : -80]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches ? 0 : 60]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const handleBuyNow = () => {
    document.getElementById('product')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLearnMore = () => {
    document.getElementById('problem')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={ref}
      id="hero"
      // FIX 2: Used `100dvh` for mobile address bar stability and `overflow-x-clip` for safety
      className="relative min-h-[100dvh] lg:min-h-screen flex items-center overflow-x-clip bg-white"
      aria-label="Dr. Detoxer hero">

      {/* Background gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-full lg:w-[60%] h-full bg-gradient-to-bl from-primary-50 via-transparent to-transparent opacity-60" />
        <div className="absolute bottom-0 left-0 w-full lg:w-[40%] h-[50%] bg-gradient-to-tr from-blue-pale via-transparent to-transparent opacity-50" />
      </div>

      {/* Decorative large background text */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden"
        aria-hidden="true">

        <span
          className="hidden md:block font-display font-light italic text-primary-50 opacity-30"
          style={{ fontSize: 'clamp(8rem, 25vw, 22rem)', lineHeight: 0.85 }}>
          DETOX
        </span>
      </div>

      {/* FIX 3: Adjusted top padding to account for mobile navigation bars */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-24 lg:pt-28 pb-20 lg:pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center min-h-[80vh]">

          {/* Left: Text (Appears below image on mobile via order-2) */}
          <motion.div
            className="col-span-1 lg:col-span-6 order-2 lg:order-1 flex flex-col items-center text-center lg:items-start lg:text-left"
            style={{ y: textY }}>

            <motion.div
              custom={0}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="mb-4 lg:mb-6">

              <span className="inline-flex items-center gap-2 bg-primary-50 text-primary px-3 py-1.5 lg:px-4 lg:py-2 rounded-full text-[10px] lg:text-xs font-semibold tracking-widest uppercase border border-primary-100">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                {t('hero.badge')}
              </span>
            </motion.div>

            <motion.h1
              custom={1}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              // FIX 4: Explicitly scaled typography for mobile
              className="font-display font-light text-foreground mb-4 lg:mb-6 text-5xl sm:text-6xl lg:text-[5rem] leading-[1.1] lg:leading-[1.05]">

              {t('hero.title.wash')}{' '}
              <span className="italic pl-0.5 pr-2 text-gradient-green">{t('hero.title.smart')}</span>
              <br />
              {t('hero.title.live')}{' '}
              <span className="italic" style={{ color: 'var(--color-blue)' }}>
                {t('hero.title.safe')}
              </span>
            </motion.h1>

            <motion.p
              custom={2}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="text-muted text-base lg:text-lg leading-relaxed max-w-md lg:max-w-md mb-8 lg:mb-10 font-light mx-auto lg:mx-0">

              {t('hero.description')}
            </motion.p>

            <motion.div
              custom={3}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              // FIX 5: Forced buttons to span full width on mobile for better touch targets
              className="flex flex-col sm:flex-row w-full sm:w-auto gap-3 lg:gap-4 mb-10 lg:mb-14">

              <button
                onClick={handleBuyNow}
                className="btn-primary w-full sm:w-auto px-8 py-4 lg:px-10 lg:py-4 rounded-2xl font-semibold text-sm tracking-wide shadow-green active:scale-[0.98] transition-transform"
                aria-label="Buy Dr. Detoxer now">
                {t('hero.buyNowPrice')}
              </button>
              <button
                onClick={handleLearnMore}
                className="w-full sm:w-auto px-8 py-4 lg:px-10 lg:py-4 rounded-2xl font-semibold text-sm tracking-wide border border-border text-foreground hover:bg-surface hover:text-primary active:scale-[0.98] transition-all duration-300"
                aria-label="Learn more about Dr. Detoxer">
                {t('hero.learnMore')}
              </button>
            </motion.div>

            {/* Floating stat badges */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-3 lg:gap-4">
              {[
                { icon: natural100Icon, alt: '100% natural — Dr. Detoxer trust badge', sub: t('hero.stats.natural') },
                { icon: killGermsIcon, alt: 'Removes pesticides and germs — Dr. Detoxer trust badge', sub: t('hero.stats.pesticide') },
                { icon: skinFriendlyIcon, alt: 'Skin-friendly formula — Dr. Detoxer trust badge', sub: t('hero.stats.soak') },
                { icon: noHarshChemicalsIcon, alt: 'No harsh chemicals — Dr. Detoxer trust badge', sub: t('hero.stats.soak') },
                { icon: labTestedIcon, alt: 'Lab tested — Dr. Detoxer trust badge', sub: t('hero.stats.soak') }
              ].map((stat, i) => (
                <motion.div
                  key={`${stat.icon}-${i}`}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={floatingBadge}
                  className="  flex flex-wrap items-center gap-2 lg:gap-3  "
                >
                  <Image
                    src={stat.icon}
                    alt={stat.alt}
                    width={85}
                    height={85}
                    sizes="(max-width: 640px) 56px, (max-width: 1024px) 96px, 85px"
                    className="w-14 h-14 sm:w-24 sm:h-24  lg:w-[85px] lg:h-[85px] object-contain"
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: Product bottle (Appears first on mobile via order-1) */}
          <div className="col-span-1 lg:col-span-6 order-1 lg:order-2 flex items-center justify-center relative w-full pt-8 lg:pt-0">
            {/* Glow behind bottle */}
            <div
              className="absolute inset-0 m-auto rounded-full opacity-20 blur-3xl"
              style={{
                width: '70%',
                height: '70%',
                background: 'radial-gradient(circle, #2d6b4f 0%, #2a7fbf 50%, transparent 80%)'
              }}
              aria-hidden="true" />

            {/* FIX 6: Scaled the decorative orbiting rings down for mobile */}
            <div
              className="absolute w-64 h-64 lg:w-80 lg:h-80 rounded-full border border-primary-100 animate-rotate-slow opacity-30"
              aria-hidden="true" />

            <div
              className="absolute w-72 h-72 lg:w-96 lg:h-96 rounded-full border border-dashed border-blue-brand/20 animate-rotate-slow opacity-20"
              style={{ animationDirection: 'reverse', animationDuration: '30s' }}
              aria-hidden="true" />

            <motion.div
              className="relative z-10 animate-float w-full max-w-[280px] sm:max-w-sm lg:max-w-md mx-auto"
              style={{ y: bottleY }}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}>

              <AppImage
                src="/assets/images/hero_section.png"
                alt="Dr. Detoxer vegetable and fruit cleaning liquid bottle — natural formula"
                width={480}
                height={560}
                priority
                className="w-full mx-auto h-auto object-contain rounded-4xl lg:rounded-5xl shadow-green-lg" />

              {/* FIX 7: Tucked the floating pills inward so they don't break the mobile horizontal constraints */}
              <motion.div className="flex flex-col sm:flex-row items-center absolute -left-2  top-2 sm:top-1/4 sm:-left-20 lg:top-1/4 lg:-left-28 lg:px-0 lg:py-2.5 z-10"
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              >
                <Image
                  src="/assets/images/neem_three.png"
                  alt="Neem leaves — natural pesticide remover used in Dr. Detoxer"
                  width={144}
                  height={144}
                  sizes="(max-width: 1024px) 96px, 144px"
                  loading="lazy"
                  className="w-24 h-24 lg:w-36 lg:h-36 object-contain"
                />
                <p className="text-[10px] lg:text-xs font-semibold text-green-700 px-1.5 py-1 sm:px-3 sm:py-2 glass rounded-xl lg:rounded-2xl shadow-medium ">
                  {t('hero.ingredients.neem')}
                </p>
              </motion.div>
              <motion.div
                className="flex flex-col sm:flex-row items-center absolute -right-6 top-12 sm:right-1 sm:top-16  md:top-16 md:right-2  lg:top-20 lg:left-60 lg:px-0 lg:py-2.5 z-10"
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              >
                <p
                  className="
                    text-[10px] lg:text-xs font-semibold
                    text-yellow-300
                    px-2 py-1 sm:px-3 sm:py-2
                    rounded-xl lg:rounded-2xl
                    shadow-lg
                    bg-gradient-to-r from-green-600 via-green-500 to-green-600
                    border border-green-300/40
                  "
                >
                  {t('hero.ingredients.tagline')}
                </p>
              </motion.div>

              <motion.div className="flex flex-col sm:flex-row-reverse items-center absolute -right-8 sm:-right-16 md:-right-28 top-1/3 md:top-1/2 z-10"
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              >
                <Image
                  src="/assets/images/lemon_slice.png"
                  alt="Lemon slice — citric acid breaks down pesticides in Dr. Detoxer"
                  width={144}
                  height={144}
                  sizes="(max-width: 1024px) 96px, 144px"
                  loading="lazy"
                  className="w-24 h-24 lg:w-36 lg:h-36 object-contain"
                />
                <p className="text-[10px] lg:text-xs font-semibold text-yellow-600 px-1.5 py-1 sm:px-3 sm:py-2 glass rounded-xl lg:rounded-2xl shadow-medium ">
                  {t('hero.ingredients.lemon')}
                </p>
              </motion.div>
              <motion.div className="flex flex-col sm:flex-row items-center absolute -left-6 sm:-left-24 sm:bottom-14 lg:-left-28 bottom-10 lg:bottom-12 z-10"
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              >
                <Image
                  src="/assets/images/tulsi.png"
                  alt="Tulsi (Holy Basil) — antibacterial ingredient in Dr. Detoxer"
                  width={144}
                  height={144}
                  sizes="(max-width: 1024px) 96px, 144px"
                  loading="lazy"
                  className="w-24 h-24 lg:w-36 lg:h-36 object-contain"
                />
                <p className="text-[10px] lg:text-xs font-semibold text-primary px-1.5 py-1 sm:px-3 sm:py-2 glass rounded-xl lg:rounded-2xl shadow-medium ">
                  {t('hero.ingredients.tulsi')}
                </p>
              </motion.div>

            </motion.div>
          </div>

        </div>

        {/* Scroll indicator - hidden on very short mobile screens so it doesn't overlap content */}
        <motion.div
          className="hidden sm:flex absolute bottom-6 lg:bottom-10 left-1/2 -translate-x-1/2 flex-col items-center gap-2 lg:gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          style={{ opacity }}
          aria-hidden="true">

          <span className="text-muted text-[10px] lg:text-xs tracking-widest uppercase font-medium">{t('hero.scroll')}</span>
          <div className="w-px h-10 lg:h-14 bg-border relative overflow-hidden rounded-full">
            <div className="absolute top-0 left-0 w-full h-1/2 bg-primary rounded-full animate-scroll-line" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
